import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmActionModal } from '@/components/ui/ConfirmActionModal';
import { SessionCallResizableSplit } from '@/components/session-call/SessionCallResizableSplit';
import { P2PTranscriptionAudioCaptureDisplay } from '@/components/session-call/P2PTranscriptionAudioCaptureDisplay';
import { P2PTranscriptionCallTray } from '@/components/session-call/P2PTranscriptionCallTray';
import { P2PTranscriptionHeroCircle } from '@/components/session-call/P2PTranscriptionHeroCircle';
import { SessionCallProntuarioPanel } from '@/pages/professional/sessao-chamada/components';
import { SessionEndTransition } from '@/pages/professional/sessao-chamada/components/prontuario/session-end-transition';
import { SessionEndProntuarioReview } from '@/pages/professional/sessao-chamada/components/prontuario/session-end-review/SessionEndProntuarioReview';
import { SessionEndCallSuccess } from '@/pages/professional/sessao-chamada/components/prontuario/session-end-review/SessionEndCallSuccess';
import { peekProntuarioSessionNotes } from '@/pages/professional/sessao-chamada/utils/prontuarioSessionNotesStorage';
import {
	MedicalRecordService,
	SessionService,
	useGetSession,
	usePatchSessionEnd,
} from '@/services';
import { useDeepgramLiveTranscription } from '@/hooks/useDeepgramLiveTranscription';
import { useDeepgramTranscriptionChatMessages } from '@/hooks/useDeepgramTranscriptionChatMessages';
import { useMicrophonePreview } from '@/hooks/useMicrophonePreview';
import { useSystemAudioPreview } from '@/hooks/useSystemAudioPreview';
import { useMergedAudioVisualizationStream } from '@/hooks/useMergedAudioVisualizationStream';
import { useTranscriptionElapsedTimer } from '@/hooks/useTranscriptionElapsedTimer';
import { cn } from '@/lib/utils';
import type { TranscriptionCaptureMode } from '@/utils/captureTranscriptionAudioStream';
import {
	isSessionCallAccessState,
	isSessionTranscriptionAllowed,
	isSessionTranscriptionMock,
	PRONTUARIO_LIVE_CAPTURE_STOPPED_NOTICE,
	PRONTUARIO_TRANSCRIPTION_BADGE_HINT,
	PRONTUARIO_TRANSCRIPTION_HEADER_HINT,
	shouldShowProfessionalEndSession,
	shouldShowSessionCallProntuario,
} from '@/constants/sessionCall';
import type { SessionCallPageShellProps } from '@/components/session-call/SessionCallPageShell.custom';
import type { SessionCallProntuarioBadgeHint } from '@/pages/professional/sessao-chamada/components/prontuario/SessionCallProntuarioRecordingBadge';
import { PRONTUARIO_BG_CLASS } from '@/pages/professional/sessao-chamada/constants/prontuarioTheme';
import { resolveSessionParticipantTarget } from '@/pages/professional/sessao-chamada/utils/prontuarioPatientSummary';
import type { OpenProntuarioNavigationState } from '@/pages/professional/home/utils/professionalHomeNavigation';

const PRONTUARIO_BADGE_HINT_VISIBLE_MS = 20_000;
const PRONTUARIO_HEADER_HINT_VISIBLE_MS = 15_000;

const P2PTranscriptionOnlyPageShell = ({
	viewerRole,
	exitPath,
	setVideoCallHeaderConfig,
}: SessionCallPageShellProps) => {
	const { sessionId } = useParams<{ sessionId: string }>();
	const location = useLocation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const mainContainerRef = useRef<HTMLDivElement>(null);

	const [professionalSessionEnded, setProfessionalSessionEnded] =
		useState(false);
	const [isPostSessionTransition, setIsPostSessionTransition] =
		useState(false);
	const [postSessionPhase, setPostSessionPhase] = useState<
		'transition' | 'review' | 'success'
	>('transition');
	const [isEndSessionConfirmOpen, setIsEndSessionConfirmOpen] =
		useState(false);
	const [systemAudioPreferred, setSystemAudioPreferred] = useState(false);
	const [isProntuarioOpen, setIsProntuarioOpen] = useState(false);
	const [prontuarioPanelKey, setProntuarioPanelKey] = useState(0);
	const [prontuarioBadgeHint, setProntuarioBadgeHint] =
		useState<SessionCallProntuarioBadgeHint>('hidden');
	const [
		showProntuarioHeaderRecordingHint,
		setShowProntuarioHeaderRecordingHint,
	] = useState(false);
	const [prontuarioHeaderHintNonce, setProntuarioHeaderHintNonce] =
		useState(0);
	const [hasJoinedSession, setHasJoinedSession] = useState(false);
	const earlyMedicalRecordPrefetchRef = useRef(false);
	const postProcessPrefetchStartedRef = useRef(false);
	const hasOpenedProntuarioRef = useRef(false);
	const hasShownTimedProntuarioHintRef = useRef(false);
	const hasShownProntuarioHeaderRecordingHintRef = useRef(false);

	const accessState = isSessionCallAccessState(location.state)
		? location.state
		: null;
	const hasRoleMismatch =
		accessState !== null && accessState.viewerRole !== viewerRole;

	const { data: sessionData, isLoading: isSessionLoading } = useGetSession(
		sessionId ?? '',
		{
			enabled: !!sessionId && !hasRoleMismatch,
		},
	);

	const resolvedSessionStatus =
		accessState?.sessionStatus ?? sessionData?.status ?? null;
	const resolvedTranscriptionMock =
		accessState?.transcriptionMock ??
		isSessionTranscriptionMock(sessionData);
	const resolvedTranscriptionAllowed =
		accessState?.transcriptionAllowed ??
		isSessionTranscriptionAllowed(sessionData);
	const sessionTranscriptionAllowed =
		isSessionTranscriptionAllowed(sessionData);

	const showProfessionalEndSession = shouldShowProfessionalEndSession({
		viewerRole,
		sessionStatus: resolvedSessionStatus,
		professionalSessionEnded,
	});

	const sessionStartMs = sessionData?.selected_date
		? new Date(sessionData.selected_date).getTime()
		: null;
	const sessionStartedAtMs = sessionData?.started_at
		? new Date(sessionData.started_at).getTime()
		: sessionStartMs;

	const transcriptionEnabled =
		hasJoinedSession &&
		resolvedSessionStatus === 'STARTED' &&
		!professionalSessionEnded &&
		!isPostSessionTransition;

	const {
		status: deepgramStatus,
		captureMode,
		micStream,
		systemStream,
		mixedStream,
		interimTranscript,
		lastFinalSegment,
		finalSegmentCounter,
		isRecording,
		lastError: deepgramError,
		start: startDeepgramTranscription,
		retry: retryDeepgramTranscription,
		replaceSystemAudioSource,
		removeSystemAudio,
		flushPendingFragments,
	} = useDeepgramLiveTranscription({
		sessionId: sessionId ?? '',
		enabled: transcriptionEnabled,
		sessionStartedAtMs,
	});

	const micPreviewEnabled =
		transcriptionEnabled && deepgramStatus === 'idle' && !micStream;
	const {
		stream: previewMicStream,
		error: micPreviewError,
		release: releaseMicPreview,
		discard: discardMicPreview,
	} = useMicrophonePreview({
		enabled: micPreviewEnabled,
	});

	const {
		stream: previewSystemStream,
		error: systemPreviewError,
		isActivating: isSystemPreviewActivating,
		activate: activateSystemPreview,
		replace: replaceSystemPreview,
		deactivate: deactivateSystemPreview,
		release: releaseSystemPreview,
	} = useSystemAudioPreview();

	const isTranscribing = deepgramStatus === 'connected';
	const transcriptionElapsed = useTranscriptionElapsedTimer(isTranscribing);
	const displayMicStream = micStream ?? previewMicStream;
	const displaySystemStream = systemStream ?? previewSystemStream;

	const previewHeroStream = useMergedAudioVisualizationStream({
		micStream: displayMicStream,
		systemStream: displaySystemStream,
		enabled:
			!mixedStream &&
			(deepgramStatus === 'connected' || deepgramStatus === 'idle'),
	});

	const heroAudioStream =
		mixedStream ??
		previewHeroStream ??
		displayMicStream ??
		displaySystemStream;

	const {
		messages: transcriptionMessages,
		liveCaptureEnabled: isProntuarioLiveCaptureEnabled,
		stoppedNotice: prontuarioTranscriptionStoppedNotice,
		clearMessages: clearProntuarioTranscription,
		stopLiveCapture: stopProntuarioLiveCapture,
		resumeLiveCapture: resumeProntuarioLiveCapture,
	} = useDeepgramTranscriptionChatMessages({
		lastFinalSegment,
		finalSegmentCounter,
		interimTranscript,
		enabled: isTranscribing && sessionTranscriptionAllowed,
	});

	const { mutate: endSession, isPending: isEndingProfessionalSession } =
		usePatchSessionEnd();

	const prefetchPatientId = useMemo(
		() =>
			sessionData
				? resolveSessionParticipantTarget(sessionData).patientId ?? null
				: null,
		[sessionData],
	);

	useEffect(() => {
		if (hasRoleMismatch) {
			navigate(exitPath, { replace: true });
		}
	}, [exitPath, hasRoleMismatch, navigate]);

	useEffect(() => {
		if (isSessionLoading || !sessionData) return;
		if (resolvedSessionStatus === 'STARTED') {
			setHasJoinedSession(true);
		}
	}, [isSessionLoading, resolvedSessionStatus, sessionData]);

	useEffect(() => {
		if (systemStream) {
			setSystemAudioPreferred(true);
		}
	}, [systemStream]);

	useEffect(() => {
		if (previewSystemStream) {
			setSystemAudioPreferred(true);
		}
	}, [previewSystemStream]);

	const goBack = useCallback(() => {
		navigate(exitPath);
	}, [exitPath, navigate]);

	const handleOpenProntuario = useCallback(() => {
		hasOpenedProntuarioRef.current = true;
		setProntuarioPanelKey((current) => current + 1);
		setIsProntuarioOpen(true);
		setProntuarioBadgeHint('hidden');
	}, []);

	const handleMinimizeProntuario = useCallback(() => {
		setIsProntuarioOpen(false);
	}, []);

	const handleToggleProntuario = useCallback(() => {
		if (isProntuarioOpen) {
			handleMinimizeProntuario();
			return;
		}
		handleOpenProntuario();
	}, [handleMinimizeProntuario, handleOpenProntuario, isProntuarioOpen]);

	const handleClearProntuarioTranscription = useCallback(() => {
		clearProntuarioTranscription();
	}, [clearProntuarioTranscription]);

	const handleStopProntuarioLiveCapture = useCallback(() => {
		stopProntuarioLiveCapture(PRONTUARIO_LIVE_CAPTURE_STOPPED_NOTICE);
	}, [stopProntuarioLiveCapture]);

	const handleResumeProntuarioLiveCapture = useCallback(() => {
		resumeProntuarioLiveCapture();
	}, [resumeProntuarioLiveCapture]);

	const dismissProntuarioBadgeHint = useCallback(() => {
		setProntuarioBadgeHint('hidden');
	}, []);

	const dismissProntuarioHeaderRecordingHint = useCallback(() => {
		setShowProntuarioHeaderRecordingHint(false);
	}, []);

	const handleEndProfessionalSession = useCallback(async () => {
		if (!sessionId || professionalSessionEnded) return;

		await flushPendingFragments();

		const rawNotes = peekProntuarioSessionNotes(
			sessionId,
			sessionData?.selected_date,
		);

		endSession(
			{
				id: sessionId,
				raw_notes: (rawNotes ?? '').trim() || undefined,
			},
			{
				onSuccess: () => {
					setProfessionalSessionEnded(true);
					setIsPostSessionTransition(true);
					setPostSessionPhase('transition');
				},
			},
		);
	}, [
		endSession,
		flushPendingFragments,
		professionalSessionEnded,
		sessionData?.selected_date,
		sessionId,
	]);

	const handlePostSessionTransitionComplete = useCallback(() => {
		setPostSessionPhase('review');
	}, []);

	const handleProcessingDone = useCallback(() => {
		if (postProcessPrefetchStartedRef.current) return;
		postProcessPrefetchStartedRef.current = true;

		if (!prefetchPatientId || !sessionId) return;

		void queryClient.invalidateQueries({
			queryKey: ['medical-record', prefetchPatientId],
		});
		void queryClient.invalidateQueries({ queryKey: ['sessions', true] });
		void queryClient.invalidateQueries({
			queryKey: ['medical-record-session'],
		});
		void queryClient.invalidateQueries({
			queryKey: ['session', sessionId],
		});

		void (async () => {
			try {
				const [record, sessions] = await Promise.all([
					queryClient.fetchQuery({
						queryKey: ['medical-record', prefetchPatientId],
						queryFn: () =>
							MedicalRecordService.getByPatient(
								prefetchPatientId,
							),
						staleTime: 0,
					}),
					queryClient.fetchQuery({
						queryKey: [
							'sessions',
							true,
							undefined,
							undefined,
							undefined,
							undefined,
							prefetchPatientId,
							undefined,
							50,
							1,
							undefined,
							undefined,
						],
						queryFn: () =>
							SessionService.getMe({
								patient_id: prefetchPatientId,
								page: 1,
								limit: 50,
							}),
						staleTime: 0,
					}),
				]);

				const currentSession = sessions.data.find(
					(item) => item.id === sessionId,
				);
				const recordSessionId =
					currentSession?.medical_record_session?.id ?? null;

				if (currentSession) {
					queryClient.setQueryData(
						['session', sessionId],
						currentSession,
					);
				}

				if (!record?.id || !recordSessionId) return;

				await queryClient.fetchQuery({
					queryKey: [
						'medical-record-session',
						record.id,
						recordSessionId,
						'post-process',
					],
					queryFn: () =>
						MedicalRecordService.getSession(
							record.id,
							recordSessionId,
						),
					staleTime: 0,
				});
			} catch {
				// revisão fará polling se o prefetch falhar
			}
		})();
	}, [prefetchPatientId, queryClient, sessionId]);

	const handleViewProntuarioFromReview = useCallback(
		(options: OpenProntuarioNavigationState) => {
			navigate(exitPath, { state: { openProntuario: options } });
		},
		[exitPath, navigate],
	);

	useEffect(() => {
		if (
			!sessionId ||
			earlyMedicalRecordPrefetchRef.current ||
			viewerRole !== 'professional'
		) {
			return;
		}

		const target = resolveSessionParticipantTarget(sessionData);
		if (!target?.patientId) return;

		earlyMedicalRecordPrefetchRef.current = true;
		void MedicalRecordService.getByPatient(target.patientId).catch(
			() => undefined,
		);
	}, [sessionData, sessionId, viewerRole]);

	const showProfessionalProntuario = shouldShowSessionCallProntuario(
		viewerRole,
		{
			sessionType: 'P2P_TRANSCRIPTION_ONLY',
			isOnline: false,
			transcriptionAllowed: resolvedTranscriptionAllowed,
			transcriptionMock: resolvedTranscriptionMock,
			hasJoinedCall: hasJoinedSession,
		},
	);

	useEffect(() => {
		if (
			!showProfessionalProntuario ||
			!hasJoinedSession ||
			isProntuarioOpen ||
			hasOpenedProntuarioRef.current ||
			hasShownTimedProntuarioHintRef.current
		) {
			return;
		}

		hasShownTimedProntuarioHintRef.current = true;
		setProntuarioBadgeHint('initial');
	}, [hasJoinedSession, isProntuarioOpen, showProfessionalProntuario]);

	useEffect(() => {
		if (prontuarioBadgeHint === 'hidden') return;
		const timeoutId = window.setTimeout(
			() => setProntuarioBadgeHint('hidden'),
			PRONTUARIO_BADGE_HINT_VISIBLE_MS,
		);
		return () => window.clearTimeout(timeoutId);
	}, [prontuarioBadgeHint]);

	useEffect(() => {
		if (
			!showProfessionalProntuario ||
			!hasJoinedSession ||
			isProntuarioOpen ||
			hasShownProntuarioHeaderRecordingHintRef.current
		) {
			return;
		}

		hasShownProntuarioHeaderRecordingHintRef.current = true;
		setShowProntuarioHeaderRecordingHint(true);
		setProntuarioHeaderHintNonce((current) => current + 1);
	}, [hasJoinedSession, isProntuarioOpen, showProfessionalProntuario]);

	useEffect(() => {
		if (!showProntuarioHeaderRecordingHint) return;
		const timeoutId = window.setTimeout(
			() => setShowProntuarioHeaderRecordingHint(false),
			PRONTUARIO_HEADER_HINT_VISIBLE_MS,
		);
		return () => window.clearTimeout(timeoutId);
	}, [prontuarioHeaderHintNonce, showProntuarioHeaderRecordingHint]);

	useLayoutEffect(() => {
		setVideoCallHeaderConfig({
			onBack: goBack,
			onToggleLayout: () => undefined,
			activeSpeakerMode: true,
			controlsEnabled: false,
			showLayoutToggle: false,
			hideBackButton: true,
			meetingScheduledStartMs: null,
			hideMeetingTimer: true,
		});

		return () => setVideoCallHeaderConfig(null);
	}, [goBack, setVideoCallHeaderConfig]);

	const handleStartTranscription = useCallback(() => {
		const useSystemAudio = systemAudioPreferred || !!previewSystemStream;
		const mode: TranscriptionCaptureMode = useSystemAudio
			? 'microphone-and-system'
			: 'microphone';

		if (previewMicStream && (!useSystemAudio || previewSystemStream)) {
			const micStreamForCapture = previewMicStream;
			const systemStreamForCapture = previewSystemStream;
			releaseMicPreview();
			if (systemStreamForCapture) {
				releaseSystemPreview();
			}
			startDeepgramTranscription(mode, {
				micStream: micStreamForCapture,
				systemStream: systemStreamForCapture,
			});
			return;
		}

		discardMicPreview();
		deactivateSystemPreview();
		startDeepgramTranscription(mode);
	}, [
		deactivateSystemPreview,
		discardMicPreview,
		previewMicStream,
		previewSystemStream,
		releaseMicPreview,
		releaseSystemPreview,
		startDeepgramTranscription,
		systemAudioPreferred,
	]);

	const handleEnableSystemAudio = useCallback(() => {
		if (deepgramStatus === 'connected') {
			void replaceSystemAudioSource();
			return;
		}
		setSystemAudioPreferred(true);
		void activateSystemPreview();
	}, [activateSystemPreview, deepgramStatus, replaceSystemAudioSource]);

	const handleReplaceSystemAudio = useCallback(() => {
		if (deepgramStatus === 'connected') {
			void replaceSystemAudioSource();
			return;
		}
		void replaceSystemPreview();
	}, [deepgramStatus, replaceSystemAudioSource, replaceSystemPreview]);

	const handleRemoveSystemAudio = useCallback(() => {
		if (deepgramStatus === 'connected') {
			removeSystemAudio();
		} else {
			deactivateSystemPreview();
		}
		setSystemAudioPreferred(false);
	}, [deactivateSystemPreview, deepgramStatus, removeSystemAudio]);

	if (!sessionId || hasRoleMismatch) {
		return null;
	}

	const isProntuarioSplitOpen =
		isProntuarioOpen && showProfessionalProntuario;

	const connectionLabel =
		deepgramStatus === 'connected'
			? isRecording
				? 'Transcrevendo'
				: 'Conectado'
			: deepgramStatus === 'connecting' ||
			    deepgramStatus === 'reconnecting'
			  ? 'Conectando transcrição...'
			  : deepgramStatus === 'error'
			    ? 'Erro na transcrição'
			    : deepgramStatus === 'idle'
			      ? 'Pronto para transcrever'
			      : 'Preparando...';

	const connectionDescription =
		deepgramStatus === 'idle'
			? 'Clique em Iniciar para escolher como captar o áudio e começar a transcrição.'
			: deepgramStatus === 'error'
			  ? 'Verifique as permissões de microfone e tente novamente.'
			  : captureMode === 'microphone'
			    ? 'Transcrição ativa pelo microfone.'
			    : captureMode === 'microphone-and-system'
			      ? 'Transcrição ativa pelo microfone e pelo áudio do computador.'
			      : 'Consulta com transcrição em tempo real.';

	const isDeepgramBusy =
		deepgramStatus === 'connecting' || deepgramStatus === 'reconnecting';

	const isCaptureVisualizerActive =
		deepgramStatus === 'connected' || deepgramStatus === 'idle';

	const showTrayControls =
		transcriptionEnabled &&
		(deepgramStatus === 'idle' ||
			deepgramStatus === 'error' ||
			deepgramStatus === 'connected' ||
			deepgramStatus === 'connecting' ||
			deepgramStatus === 'reconnecting' ||
			showProfessionalEndSession);

	return (
		<div
			className={cn(
				'relative flex h-full min-h-0 w-full flex-col overflow-hidden',
				showProfessionalProntuario && PRONTUARIO_BG_CLASS,
			)}
		>
			<SessionCallResizableSplit
				callContainerRef={mainContainerRef}
				callPane={
					<div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-gradient-to-b from-[#243447] via-[#1f2d3d] to-[#243447] text-white">
						<div className="flex min-h-0 flex-1 flex-col justify-between px-4 pb-4 pt-40 text-center">
							<div className="flex flex-col items-center gap-4">
								<P2PTranscriptionHeroCircle
									stream={heroAudioStream}
									isIdle={deepgramStatus === 'idle'}
									isActive={isTranscribing}
									isBusy={isDeepgramBusy}
									onClick={handleStartTranscription}
									disabled={isEndingProfessionalSession}
								/>

								<div className="w-full max-w-lg space-y-2">
									{isTranscribing ? (
										<p
											className="font-mono text-4xl font-bold tracking-wider text-white sm:text-5xl"
											aria-live="polite"
											aria-label={`Tempo de transcrição: ${transcriptionElapsed}`}
										>
											{transcriptionElapsed}
										</p>
									) : null}
									<p className="text-lg font-semibold sm:text-xl">
										{connectionLabel}
									</p>
									<p className="text-sm leading-relaxed text-zinc-400">
										{connectionDescription}
									</p>
									{micPreviewError &&
									deepgramStatus === 'idle' ? (
										<p className="text-xs text-amber-200">
											{micPreviewError}
										</p>
									) : null}
									{systemPreviewError &&
									deepgramStatus === 'idle' ? (
										<p className="text-xs text-amber-200">
											{systemPreviewError}
										</p>
									) : null}
									{deepgramError ? (
										<p className="text-xs text-red-300">
											{deepgramError}
										</p>
									) : null}
								</div>
							</div>

							<div className="flex w-full justify-center">
								<P2PTranscriptionAudioCaptureDisplay
									micStream={micStream}
									systemStream={displaySystemStream}
									previewMicStream={previewMicStream}
									isActive={isCaptureVisualizerActive}
									isSystemActivating={
										isSystemPreviewActivating
									}
									onReplaceSystemAudio={
										handleReplaceSystemAudio
									}
									onRemoveSystemAudio={
										handleRemoveSystemAudio
									}
									onEnableSystemAudio={
										handleEnableSystemAudio
									}
								/>
							</div>
						</div>

						{showTrayControls ? (
							<P2PTranscriptionCallTray
								showStart={deepgramStatus === 'idle'}
								showRetry={deepgramStatus === 'error'}
								showEndSession={showProfessionalEndSession}
								showProntuario={showProfessionalProntuario}
								isProntuarioOpen={isProntuarioSplitOpen}
								isStarting={isDeepgramBusy}
								isEnding={isEndingProfessionalSession}
								onStart={handleStartTranscription}
								onRetry={retryDeepgramTranscription}
								onEndSession={() =>
									setIsEndSessionConfirmOpen(true)
								}
								onToggleProntuario={handleToggleProntuario}
							/>
						) : null}
					</div>
				}
				prontuarioOpen={isProntuarioSplitOpen}
				prontuarioEnabled={showProfessionalProntuario}
				showProntuarioBadge={
					showProfessionalProntuario && !isProntuarioOpen
				}
				onOpenProntuario={handleOpenProntuario}
				prontuarioBadgeHint={prontuarioBadgeHint}
				prontuarioBadgeHintMessage={PRONTUARIO_TRANSCRIPTION_BADGE_HINT}
				transcriptionActive={sessionTranscriptionAllowed}
				onDismissProntuarioBadgeHint={dismissProntuarioBadgeHint}
				prontuarioPane={
					<SessionCallProntuarioPanel
						key={prontuarioPanelKey}
						session={sessionData}
						className="h-full w-full"
						onMinimize={handleMinimizeProntuario}
						showHeaderRecordingHint={
							showProntuarioHeaderRecordingHint
						}
						recordingHintMessage={
							PRONTUARIO_TRANSCRIPTION_HEADER_HINT
						}
						recordingHintKey={prontuarioHeaderHintNonce}
						transcriptionActive={sessionTranscriptionAllowed}
						isProntuarioLiveCaptureEnabled={
							isProntuarioLiveCaptureEnabled
						}
						prontuarioTranscriptionStoppedNotice={
							prontuarioTranscriptionStoppedNotice
						}
						onDismissHeaderRecordingHint={
							dismissProntuarioHeaderRecordingHint
						}
						transcriptionMessages={transcriptionMessages}
						onClearProntuarioTranscription={
							handleClearProntuarioTranscription
						}
						onStopProntuarioLiveCapture={
							handleStopProntuarioLiveCapture
						}
						onResumeProntuarioLiveCapture={
							handleResumeProntuarioLiveCapture
						}
					/>
				}
			/>

			{isPostSessionTransition &&
				postSessionPhase === 'transition' &&
				sessionId && (
					<SessionEndTransition
						sessionId={sessionId}
						onComplete={handlePostSessionTransitionComplete}
						onProcessingDone={handleProcessingDone}
					/>
				)}

			{isPostSessionTransition &&
				postSessionPhase === 'review' &&
				sessionData && (
					<SessionEndProntuarioReview
						session={sessionData}
						onGoHome={goBack}
						onViewProntuario={handleViewProntuarioFromReview}
					/>
				)}

			{isPostSessionTransition && postSessionPhase === 'success' && (
				<SessionEndCallSuccess onGoHome={goBack} />
			)}

			<ConfirmActionModal
				isOpen={isEndSessionConfirmOpen}
				onClose={() => setIsEndSessionConfirmOpen(false)}
				onConfirm={() => {
					setIsEndSessionConfirmOpen(false);
					void handleEndProfessionalSession();
				}}
				title="Encerrar sessão"
				description="Você tem certeza que deseja encerrar a sessão? A transcrição será finalizada e o prontuário seguirá para processamento."
				confirmButtonText={
					isEndingProfessionalSession
						? 'Encerrando...'
						: 'Encerrar sessão'
				}
				disableConfirm={isEndingProfessionalSession}
				tone="neutral"
			/>
		</div>
	);
};

export { P2PTranscriptionOnlyPageShell };
